//Built-in libraries

//Third party libraries
import {
  ClipboardDocumentListIcon,
  PencilIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  UserCircleIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { ArchiveBoxIcon, ArrowsRightLeftIcon, CogIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
//Custom components
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import Confirm from '@/components/UI/Modal/Confirm';
import { patchArchiveProject, patchUnarchiveProject } from '../../../../../../../api/Sherpa/Project';
//Services
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { usePermission } from '../../../../../../../hooks/usePermission';
import { notifyUser } from '../../../../../../../store/Toast';
import { sanitizeResponseError } from '../../../../../../../utils';
import ChangeProjectLead from './ChangeProjectLead';
import ChangeProjectStatus from './ChangeProjectStatus';
import ChangeProjectThumbnail from './ChangeProjectThumbnail';
import ChangeProjectWorkspace from './ChangeProjectWorkspace';
import UpdateProjectBaseline from './UpdateProjectBaseline';

//Assets and Miscellaneous

const ProjectSettings = () => {
  const { projectId } = useParams<string>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({
    projectLead: false,
    projectStatus: false,
    projectThumbnail: false,
    projectWorkspace: false,
  });
  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const project = fetchProject?.data?.data;

  const archiveProject = useMutation(() => patchArchiveProject({ projectId: Number(projectId) }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-projects']);
      setShowDeleteConfirm(false);
      navigate('/app/sherpa/projects', { replace: true });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Archive successful',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const unarchiveProject = useMutation(() => patchUnarchiveProject({ projectId: Number(projectId) }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-projects']);
      setShowUnarchiveConfirm(false);
      navigate('/app/sherpa/projects', { replace: true });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Unarchive successful',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const projectAuditHandler = () => {
    navigate(`/app/sherpa/projects/${project?.id.toString()}/audit`);
  };

  const projectReportHandler = () => {
    navigate(`/app/sherpa/projects/${project?.id.toString()}/reports`);
  };

  const dropDownItemModalHandler = (name: string) => {
    setIsEditing((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const EditButtonDropDownItems: DropDownMenuItem[] = [
    {
      name: 'Edit Project Info',
      clicked: () => dropDownItemModalHandler('projectBaseline'),
      icon: <PencilIcon className='h-5 w-5' />,
      permission: {
        of: 'UPDATE_PROJECT',
      },
    },
    {
      name: 'Change Project Lead',
      clicked: () => dropDownItemModalHandler('projectLead'),
      icon: <UserCircleIcon className='h-6 w-6' />,
      permission: {
        of: 'UPDATE_PROJECT',
      },
    },
    {
      name: 'Change Status',
      clicked: () => dropDownItemModalHandler('projectStatus'),
      icon: <ArrowsRightLeftIcon className='h-5 w-5' />,
      permission: {
        of: 'UPDATE_PROJECT_STATUS',
      },
    },
    {
      name: 'Change Thumbnail Picture',
      clicked: () => dropDownItemModalHandler('projectThumbnail'),
      icon: <PhotoIcon className='h-8 w-8' />,
      permission: {
        of: 'UPDATE_PROJECT',
      },
    },
    {
      name: 'Change Project workspace',
      clicked: () => dropDownItemModalHandler('projectWorkspace'),
      icon: <ViewColumnsIcon className='h-8 w-8' />,
      permission: {
        of: 'UPDATE_PROJECT',
      },
    },
    {
      name: 'Project Audit',
      clicked: projectAuditHandler,
      icon: <ClipboardDocumentListIcon className='h-5 w-5' />,
      permission: {
        of: 'READ_PROJECT',
      },
    },
    // {
    //   name: 'Reports',
    //   clicked: projectReportHandler,
    //   icon: <PresentationChartBarIcon className='h-5 w-5' />,
    //   permission: {
    //     of: 'READ_PROJECT',
    //   },
    // },
    {
      name: 'Archive Project',
      clicked: () => setShowDeleteConfirm(true),
      icon: <ArchiveBoxIcon className='h-5 w-5' />,
      permission: {
        of: 'ARCHIVE_PROJECT',
      },
    },
  ];

  const archivedProjectDropDownItems: DropDownMenuItem[] = [
    {
      name: 'Unarchive Project',
      clicked: () => setShowUnarchiveConfirm(true),
      icon: <ArchiveBoxIcon className='h-5 w-5' />,
      permission: {
        of: 'UNARCHIVE_PROJECT',
      },
    },
  ];

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Archive Project'
          message={`You are about to archive ${project?.name} project. Are you sure you want to archive ?`}
          acceptButtonLabel={`${archiveProject.isLoading ? 'Archiving...' : 'Archive'}`}
          declineButtonLabel='Cancel'
          accept={archiveProject.mutate}
          decline={() => setShowDeleteConfirm(false)}
        />
      )}
      {showUnarchiveConfirm && (
        <Confirm
          title='Unarchive Project'
          message={`You are about to unarchive ${project?.name} project. Are you sure you want to unarchive ?`}
          acceptButtonLabel={`${unarchiveProject.isLoading ? 'Unarchiving...' : 'Unarchive'}`}
          declineButtonLabel='Cancel'
          accept={unarchiveProject.mutate}
          decline={() => setShowUnarchiveConfirm(false)}
        />
      )}
      {isEditing.projectBaseline && <UpdateProjectBaseline handleModal={() => dropDownItemModalHandler('projectBaseline')} />}
      {isEditing.projectLead && <ChangeProjectLead handleModal={() => dropDownItemModalHandler('projectLead')} />}
      {isEditing.projectStatus && <ChangeProjectStatus handleModal={() => dropDownItemModalHandler('projectStatus')} />}
      {isEditing.projectWorkspace && <ChangeProjectWorkspace handleModal={() => dropDownItemModalHandler('projectWorkspace')} />}
      {isEditing.projectThumbnail && <ChangeProjectThumbnail handleModal={() => dropDownItemModalHandler('projectThumbnail')} />}
      {hasPermission(['UPDATE_PROJECT', 'ARCHIVE_PROJECT', 'UPDATE_PROJECT_SOURCE', 'UPDATE_PROJECT_STATUS', 'READ_PROJECT']) &&
        !project?.archived && (
          <div className='absolute top-2 -left-6'>
            <DropDown menuItems={EditButtonDropDownItems} dropDownDirection='left'>
              <div className='bg-primaryColor-normal p-2 rounded hover:bg-primaryColor-normal cursor-pointer transition duration-300 ease-in-out text-white gap-2'>
                <CogIcon className='h-6 w-6 ' />
              </div>
            </DropDown>
          </div>
        )}
      {hasPermission(['UNARCHIVE_PROJECT']) && project?.archived && (
        <div className='absolute top-2 -left-6'>
          <DropDown menuItems={archivedProjectDropDownItems} dropDownDirection='left'>
            <div className='bg-primaryColor-normal p-2 rounded hover:bg-primaryColor-normal cursor-pointer transition duration-300 ease-in-out text-white gap-2'>
              <CogIcon className='h-6 w-6' />
            </div>
          </DropDown>
        </div>
      )}
    </>
  );
};

export default ProjectSettings;
